<script>
    import {_, number} from 'svelte-i18n';
    import ItemsListing from '../ItemsListing.svelte';
    import {resourceDetails} from '../../globals.js';
    import {claimPrize} from './api.js';
    import {now} from './now.js';
    import checkmark from '../../assets/gold_rush/green_checkmark.png';
    import chestGlow from '../../assets/gold_rush/chest_glow.png?format=webp;jpg';

    let { milestone, event, onclaimed, onunlockpremium } = $props();

    const itemGlobals = { resourceDetails };
    const lockIcon = '/images/advancement-rewards/lock.png';

    let processing = $state(false);

    let claimWindowOpen = $derived($now < new Date(event.claimBy).getTime());
    let freeClaimable = $derived(milestone.reached && !milestone.claimed);
    let premiumClaimable = $derived(milestone.reached && event.hasEventPass && !milestone.seasonPassClaimed);
    let claimable = $derived(claimWindowOpen && (freeClaimable || premiumClaimable));

    async function claim() {
        if (processing || !claimable) return;
        processing = true;
        // Optimistic: claimPrize marks the milestone claimed locally and fires
        // the grant; we don't gate the UI on the response.
        const result = await claimPrize(milestone.id);
        processing = false;
        onclaimed?.(result.grantedItems);
    }
</script>

<div class="milestone" class:current={milestone.isCurrent} class:reached={milestone.reached}>
    <div class="reward-card free"
         class:locked={!milestone.reached}
         class:claimed={milestone.claimed}>
        <span class="lane-label">{$_('WebBattlePassFree', { default: 'Free' })}</span>
        {#if milestone.claimed}
            <img class="claimed-stamp" src={checkmark} alt={$_('Collected', { default: 'Collected' })}>
        {:else if !milestone.reached}
            <img class="lock-icon" src={lockIcon} alt="">
        {/if}
        <ItemsListing globals={itemGlobals} goldAmount={0} items={milestone.items}
                      storePack={{}} hasMask={false}/>
    </div>

    <div class="node-wrapper">
        {#if milestone.isCurrent}
            <picture class="node-glow">
                <source srcset={chestGlow[0]} type="image/webp">
                <source srcset={chestGlow[1]} type="image/jpg">
                <img alt="" src={chestGlow[0]}>
            </picture>
        {/if}
        <div class="node">
            <span class="threshold">{$number(milestone.pointsNeeded)}</span>
        </div>
    </div>

    <div class="reward-card premium"
         class:locked={milestone.reached && event.hasEventPass === false}
         class:dimmed={!milestone.reached}
         class:claimed={milestone.seasonPassClaimed}>
        <span class="lane-label premium-tag">{$_('WebBattlePassPremium', { default: 'Premium' })}</span>
        {#if milestone.seasonPassClaimed}
            <img class="claimed-stamp" src={checkmark} alt={$_('Collected', { default: 'Collected' })}>
        {:else if !milestone.reached || !event.hasEventPass}
            <img class="lock-icon" src={lockIcon} alt="">
        {/if}
        <ItemsListing globals={itemGlobals} goldAmount={0} items={milestone.seasonPass}
                      storePack={{}} hasMask={false}/>
        {#if !event.hasEventPass && !milestone.seasonPassClaimed}
            <button type="button" class="unlock-strip" onclick={() => onunlockpremium?.()}>
                <img src={lockIcon} alt="">
                {$_('WebBattlePassUnlockPremium', { default: 'Unlock Premium' })}
            </button>
        {/if}
    </div>

    <button type="button" class="button primary claim-button"
            class:green={claimable && !processing}
            disabled={!claimable || processing}
            onclick={claim}>
        {#if processing}
            <span class="bp-spinner"></span>
        {:else if milestone.claimed && (!event.hasEventPass || milestone.seasonPassClaimed)}
            {$_('Collected', { default: 'Collected' })}
        {:else}
            {$_('EventClaimButton', { default: 'Claim' })}
        {/if}
    </button>
</div>

<style lang="scss">
  @use './battlepass.scss' as bp;

  .milestone {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 230px;
    min-width: 230px;
    padding: 14px 8px 12px;
    gap: 10px;
    position: relative;
    border-radius: 12px;
    transition: background 0.3s ease;

    &.current {
      background: radial-gradient(ellipse at center, rgba(216, 107, 11, 0.18) 0%, transparent 70%);
    }

    .node-wrapper {
      position: relative;
      height: 56px;
      display: flex;
      align-items: center;
      justify-content: center;

      .node-glow {
        position: absolute;
        width: 120px;
        height: 116px;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
        animation: bp-glow-pulse 2.2s ease-in-out infinite;

        img {
          width: 100%;
          height: 100%;
        }
      }

      .node {
        position: relative;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: radial-gradient(ellipse at 35% 30%, #5a2c0e 0%, #240c05 80%);
        border: 2px solid #8a6228;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 227, 132, 0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;

        .threshold {
          font-family: var(--font-family-sans-condensed);
          font-weight: bold;
          font-size: 14px;
          color: rgb(255, 217, 87);
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
        }
      }
    }

    &.reached .node-wrapper .node {
      background: radial-gradient(ellipse at 35% 30%, #ffcc00 5%, #ff7700 90%);
      border-color: #fff;
      box-shadow: 0 0 14px rgba(255, 140, 0, 0.7), 0 3px 6px rgba(0, 0, 0, 0.35);

      .threshold {
        color: #4a2408;
        text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
      }
    }

    .reward-card {
      position: relative;
      width: 100%;
      min-height: 88px;
      border-radius: 10px;
      background: linear-gradient(180deg, #fdf3cf 0%, #f3dfa0 100%);
      border: 1px solid #b89146;
      box-shadow: 0 2px 5px rgba(60, 30, 5, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.6);
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      padding: 22px 8px 8px;

      .lane-label {
        position: absolute;
        top: 0;
        left: 0;
        padding: 2px 12px 3px 10px;
        font-family: var(--font-family-sans-condensed);
        font-size: 11px;
        font-weight: bold;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #fdf3cf;
        background: #8a6228;
        border-radius: 9px 0 9px 0;
        z-index: 2;

        &.premium-tag {
          color: #2a1004;
          background: linear-gradient(110deg, #ffcc00 0%, #ff9d2e 100%);
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
        }
      }

      &.premium {
        background: linear-gradient(180deg, #ffe9b0 0%, #f5cd72 100%);
        border: 2px solid #d8a93c;
        box-shadow: 0 2px 6px rgba(60, 30, 5, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.7);
        padding-bottom: 4px;
      }

      &.locked :global(.item),
      &.dimmed :global(.item) {
        filter: saturate(0.55);
        opacity: 0.8;
      }

      &.claimed {
        opacity: 0.72;
      }

      .lock-icon {
        position: absolute;
        top: -8px;
        right: -7px;
        width: 26px;
        height: auto;
        z-index: 3;
        filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.45));
      }

      .claimed-stamp {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 48px;
        height: auto;
        transform: translate(-50%, -50%) rotate(-12deg);
        z-index: 3;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
      }

      .unlock-strip {
        display: flex !important;
        align-items: center;
        justify-content: center;
        text-align: center;
        line-height: 1;
        gap: 6px;
        width: 100%;
        margin: 4px 0 0;
        padding: 5px 6px;
        min-height: 30px;
        border: none;
        border-radius: 6px;
        background: linear-gradient(180deg, #ffcc00 0%, #ff9d2e 100%);
        box-shadow: 0 2px 3px rgba(60, 30, 5, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.5);
        font-family: var(--font-family-sans-condensed);
        font-size: 13px;
        font-weight: bold;
        color: #4a2408;
        cursor: pointer;
        transition: filter 0.15s ease;

        &:hover {
          filter: brightness(1.08);
        }

        img {
          width: 14px;
          height: auto;
        }
      }

      :global(.items),
      :global(.rewards) {
        background: none;
        margin: 0;
        width: 100%;
      }

      :global(.item) {
        border-bottom: none;
      }
    }

    .claim-button {
      width: 100%;
      min-height: 38px;
      font-size: 17px;
      padding: 0.3rem 0.8rem;
      font-family: var(--font-family-sans-condensed);
      letter-spacing: 0.04em;
      border-radius: 6px;

      &:disabled {
        @include bp.bp-disabled-button;
      }

      .bp-spinner {
        @include bp.bp-button-spinner;
      }
    }
  }

  @keyframes bp-spin {
    to { transform: rotate(360deg); }
  }

  @keyframes bp-glow-pulse {
    0%, 100% {
      opacity: 0.75;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.12);
    }
  }

  @media (max-width: 767px) {
    .milestone {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      width: 100%;
      min-width: 0;
      padding: 10px 8px;
      gap: 8px;

      .node-wrapper {
        order: 1;
        min-width: 56px;
      }

      .claim-button {
        order: 2;
        flex: 1;
        width: auto;
        min-height: 42px;
      }

      .reward-card {
        min-height: 0;
        padding: 24px 8px 8px;

        &.free {
          order: 3;
          width: 100%;
        }

        &.premium {
          order: 4;
          width: 100%;
        }
      }
    }
  }
</style>
